import { useState, useEffect, useCallback } from "react";
import type { CSSProperties } from "react";
import { loadHiddenUserIds, saveHiddenUserIds } from "../../lib/storage";

const API = import.meta.env.VITE_API_URL as string;
const PAGE = 20;

// ── Types ────────────────────────────────────────────────────────────────────

interface Stats {
  users: { total: number; new_7d: number; new_30d: number };
  subscriptions: { trialing: number; active: number; past_due: number; canceled: number };
  banks_linked: number;
  feedback: { new: number; total: number };
}

interface FeedbackItem {
  id: string;
  created_at: string;
  category: string;
  message: string;
  mode: string;
  user_email: string | null;
  status: "new" | "reviewed" | "resolved";
}

interface UserItem {
  id: string;
  email: string;
  created_at: string;
  subscription_status: string | null;
  has_bank: boolean;
}

type Section = "overview" | "activity" | "feedback" | "users";

interface ActivityData {
  total_events: number;
  active_users: number;
  by_event: { event: string; count: number }[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
}

const STATUS_COLOR: Record<string, string> = {
  new: "#f97316", reviewed: "#0ea5e9", resolved: "#22c55e",
  active: "#22c55e", trialing: "#0ea5e9", past_due: "#ef4444", canceled: "#64748b",
  "no sub": "#334155", "bank ✓": "#a78bfa",
};

function Pill({ label }: { label: string }) {
  const color = STATUS_COLOR[label] ?? "#64748b";
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 6,
      background: color + "22", color, border: `1px solid ${color}44`,
      textTransform: "capitalize", whiteSpace: "nowrap",
    }}>
      {label.replace("_", " ")}
    </span>
  );
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div style={{ background: "#1e293b", borderRadius: 10, padding: "14px 16px", flex: 1, minWidth: 90 }}>
      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: "#f8fafc" }}>{value}</div>
    </div>
  );
}

function ActionBtn({ label, onClick, disabled, color = "#0ea5e9" }: {
  label: string; onClick: () => void; disabled: boolean; color?: string;
}) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontSize: 12, fontWeight: 600, color, padding: "6px 10px",
      background: color + "22", borderRadius: 6, border: `1px solid ${color}44`,
      cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1,
    }}>
      {disabled ? "…" : label}
    </button>
  );
}

const paginationBtn: CSSProperties = {
  background: "#1e293b", color: "#e2e8f0", border: "1px solid #334155",
  borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600,
};

// ── Main ─────────────────────────────────────────────────────────────────────

export default function AdminDashboard({ accessToken, onBack }: { accessToken: string; onBack: () => void }) {
  const [section, setSection] = useState<Section>("overview");
  const auth = `Bearer ${accessToken}`;

  return (
    <div style={{ minHeight: "100vh", background: "#0f172a", color: "#e2e8f0" }}>
      <div style={{
        background: "#1e293b", borderBottom: "1px solid #334155",
        padding: "14px 16px", display: "flex", alignItems: "center", gap: 12,
        position: "sticky", top: 0, zIndex: 20,
      }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 0, flexShrink: 0 }}>←</button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#f8fafc" }}>Admin Dashboard</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>MoneyMission · David only</div>
        </div>
      </div>

      <div style={{ display: "flex", background: "#1e293b", borderBottom: "1px solid #334155", overflowX: "auto" }}>
        {(["overview", "activity", "feedback", "users"] as Section[]).map(s => (
          <button key={s} onClick={() => setSection(s)} style={{
            flex: 1, minWidth: 72, padding: "10px 2px", border: "none", background: "none", cursor: "pointer",
            fontSize: 11, fontWeight: 700, color: section === s ? "#0ea5e9" : "#64748b",
            borderBottom: `2px solid ${section === s ? "#0ea5e9" : "transparent"}`,
            whiteSpace: "nowrap",
          }}>
            {s === "overview" ? "📊 Stats" : s === "activity" ? "⚡ Activity" : s === "feedback" ? "💬 Feedback" : "👥 Users"}
          </button>
        ))}
      </div>

      <div style={{ padding: "16px", maxWidth: 900, margin: "0 auto" }}>
        {section === "overview" && <OverviewSection auth={auth} />}
        {section === "activity" && <ActivitySection auth={auth} />}
        {section === "feedback" && <FeedbackSection auth={auth} />}
        {section === "users"    && <UsersSection auth={auth} />}
      </div>
    </div>
  );
}

// ── Overview ─────────────────────────────────────────────────────────────────

function OverviewSection({ auth }: { auth: string }) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/api/admin/stats`, { headers: { Authorization: auth } })
      .then(r => r.json())
      .then(d => d.ok ? setStats(d) : setErr(d.error ?? "Request failed"))
      .catch(() => setErr("Network error"));
  }, [auth]);

  if (err) return <div style={{ color: "#ef4444", padding: 16 }}>Error: {err}</div>;
  if (!stats) return <div style={{ color: "#64748b", padding: 16 }}>Loading stats…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Section label="Users">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatCard label="Total" value={stats.users.total} />
          <StatCard label="New (7d)" value={stats.users.new_7d} />
          <StatCard label="New (30d)" value={stats.users.new_30d} />
        </div>
      </Section>

      <Section label="Subscriptions">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatCard label="Active" value={stats.subscriptions.active} />
          <StatCard label="Trialing" value={stats.subscriptions.trialing} />
          <StatCard label="Past Due" value={stats.subscriptions.past_due} />
          <StatCard label="Canceled" value={stats.subscriptions.canceled} />
        </div>
      </Section>

      <Section label="Engagement">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatCard label="Banks Linked" value={stats.banks_linked} />
          <StatCard label="New Feedback" value={stats.feedback.new} />
          <StatCard label="Total Feedback" value={stats.feedback.total} />
        </div>
      </Section>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{label}</div>
      {children}
    </div>
  );
}

// ── Activity ──────────────────────────────────────────────────────────────────

function ActivitySection({ auth }: { auth: string }) {
  const [data, setData] = useState<ActivityData | null>(null);
  const [days, setDays] = useState(7);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setData(null); setErr(null);
    fetch(`${API}/api/admin/activity?days=${days}`, { headers: { Authorization: auth } })
      .then(r => r.json())
      .then(d => d.ok ? setData(d) : setErr(d.error ?? "Failed"))
      .catch(() => setErr("Network error"));
  }, [auth, days]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)} style={{
            padding: "6px 14px", borderRadius: 8, border: "1px solid #334155", cursor: "pointer",
            background: days === d ? "#0ea5e9" : "#1e293b", color: days === d ? "#fff" : "#94a3b8",
            fontWeight: 700, fontSize: 13,
          }}>
            {d}d
          </button>
        ))}
        <span style={{ fontSize: 12, color: "#64748b", marginLeft: 4 }}>window</span>
      </div>

      {err && <div style={{ color: "#ef4444", fontSize: 13 }}>{err}</div>}
      {!data && !err && <div style={{ color: "#64748b", fontSize: 13 }}>Loading…</div>}

      {data && (
        <>
          <Section label="Summary">
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <StatCard label="Total Events" value={data.total_events.toLocaleString()} />
              <StatCard label="Active Users" value={data.active_users.toLocaleString()} />
            </div>
          </Section>

          <Section label="By Event">
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[...(data.by_event ?? [])].sort((a, b) => b.count - a.count).map(row => {
                const max = data.by_event[0]?.count ?? 1;
                return (
                  <div key={row.event} style={{ background: "#1e293b", borderRadius: 8, padding: "10px 14px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{row.event}</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: "#0ea5e9" }}>{row.count.toLocaleString()}</span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: "#334155" }}>
                      <div style={{ height: "100%", borderRadius: 2, background: "#0ea5e9", width: `${(row.count / max) * 100}%`, transition: "width 0.4s" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}

// ── Feedback ─────────────────────────────────────────────────────────────────

function FeedbackSection({ auth }: { auth: string }) {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    const p = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (filterStatus) p.set("status", filterStatus);
    if (filterCategory) p.set("category", filterCategory);
    fetch(`${API}/api/admin/feedback?${p}`, { headers: { Authorization: auth } })
      .then(r => r.json())
      .then(d => { if (d.ok) { setItems(d.feedback); setTotal(d.total); } else setErr(d.error ?? "Failed"); })
      .catch(() => setErr("Network error"))
      .finally(() => setLoading(false));
  }, [auth, filterStatus, filterCategory, offset]);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(id: string, newStatus: FeedbackItem["status"]) {
    setUpdating(id);
    const r = await fetch(`${API}/api/admin/feedback/${id}/status`, {
      method: "POST",
      headers: { Authorization: auth, "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus }),
    }).then(r => r.json()).catch(() => ({ ok: false }));
    if (r.ok) setItems(prev => prev.map(i => i.id === id ? { ...i, status: newStatus } : i));
    setUpdating(null);
  }

  const sel: CSSProperties = {
    flex: 1, minWidth: 120, background: "#1e293b", color: "#e2e8f0",
    border: "1px solid #334155", borderRadius: 8, padding: "8px 10px", fontSize: 13,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setOffset(0); }} style={sel}>
          <option value="">All statuses</option>
          {["new", "reviewed", "resolved"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setOffset(0); }} style={sel}>
          <option value="">All categories</option>
          {["Working well", "Confusing", "Feature idea", "Bug"].map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {err && <div style={{ color: "#ef4444", fontSize: 13 }}>{err}</div>}
      {loading && <div style={{ color: "#64748b", fontSize: 13 }}>Loading…</div>}
      <div style={{ fontSize: 11, color: "#64748b" }}>{total} submissions</div>

      {items.map(item => (
        <div key={item.id} style={{ background: "#1e293b", borderRadius: 10, padding: 14, border: "1px solid #334155" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Pill label={item.status} />
              {item.category && <Pill label={item.category} />}
              {item.mode && <Pill label={item.mode} />}
            </div>
            <div style={{ fontSize: 11, color: "#64748b", flexShrink: 0 }}>{fmtDate(item.created_at)}</div>
          </div>
          <div style={{ fontSize: 14, color: "#e2e8f0", lineHeight: 1.5, marginBottom: 10 }}>{item.message}</div>
          {item.user_email && (
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>
              From:{" "}
              <a href={`mailto:${item.user_email}?subject=Re%3A Your MoneyMission feedback`}
                style={{ color: "#0ea5e9", textDecoration: "none" }}>
                {item.user_email}
              </a>
            </div>
          )}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {item.status !== "reviewed" && (
              <ActionBtn label="Mark reviewed" disabled={updating === item.id}
                onClick={() => updateStatus(item.id, "reviewed")} />
            )}
            {item.status !== "resolved" && (
              <ActionBtn label="Mark resolved" disabled={updating === item.id}
                onClick={() => updateStatus(item.id, "resolved")} color="#22c55e" />
            )}
          </div>
        </div>
      ))}

      {total > PAGE && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => setOffset(Math.max(0, offset - PAGE))} disabled={offset === 0}
            style={{ ...paginationBtn, opacity: offset === 0 ? 0.4 : 1 }}>← Prev</button>
          <span style={{ fontSize: 12, color: "#64748b" }}>{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
          <button onClick={() => setOffset(offset + PAGE)} disabled={offset + PAGE >= total}
            style={{ ...paginationBtn, opacity: offset + PAGE >= total ? 0.4 : 1 }}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ── Users ─────────────────────────────────────────────────────────────────────

function UsersSection({ auth }: { auth: string }) {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [hiddenIds, setHiddenIds] = useState<string[]>(() => loadHiddenUserIds());
  const [showHidden, setShowHidden] = useState(false);

  function hideUser(id: string) {
    setHiddenIds(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      saveHiddenUserIds(next);
      return next;
    });
  }
  function unhideUser(id: string) {
    setHiddenIds(prev => {
      const next = prev.filter(x => x !== id);
      saveHiddenUserIds(next);
      return next;
    });
  }

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setOffset(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    const p = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (debouncedSearch) p.set("search", debouncedSearch);
    fetch(`${API}/api/admin/users?${p}`, { headers: { Authorization: auth } })
      .then(r => r.json())
      .then(d => { if (d.ok) { setUsers(d.users); setTotal(d.total); } else setErr(d.error ?? "Failed"); })
      .catch(() => setErr("Network error"))
      .finally(() => setLoading(false));
  }, [auth, debouncedSearch, offset]);

  useEffect(() => { load(); }, [load]);

  async function runAction(key: string, url: string, body: object, msg: string) {
    setActing(key);
    const r = await fetch(`${API}${url}`, {
      method: "POST",
      headers: { Authorization: auth, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(r => r.json()).catch(() => ({ ok: false }));
    setActing(null);
    setToast(r.ok ? msg : "Action failed — try again");
    setTimeout(() => setToast(null), 3000);
    if (r.ok) load();
  }

  const visibleUsers = users.filter(u => showHidden ? hiddenIds.includes(u.id) : !hiddenIds.includes(u.id));
  const hiddenOnThisPage = users.filter(u => hiddenIds.includes(u.id)).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search by email…"
        style={{
          background: "#1e293b", color: "#e2e8f0", border: "1px solid #334155",
          borderRadius: 8, padding: "10px 14px", fontSize: 14, width: "100%", boxSizing: "border-box",
        }} />

      {toast && (
        <div style={{ background: "#064e3b", color: "#6ee7b7", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>{toast}</div>
      )}
      {err && <div style={{ color: "#ef4444", fontSize: 13 }}>{err}</div>}
      {loading && <div style={{ color: "#64748b", fontSize: 13 }}>Loading…</div>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 11, color: "#64748b" }}>
          {total} users{hiddenIds.length > 0 && !showHidden && ` (${hiddenIds.length} hidden)`}
        </div>
        {hiddenIds.length > 0 && (
          <button onClick={() => setShowHidden(s => !s)} style={{
            fontSize: 11, fontWeight: 700, color: "#94a3b8", background: "none",
            border: "1px solid #334155", borderRadius: 6, padding: "4px 10px", cursor: "pointer",
          }}>
            {showHidden ? "← Back to active users" : `Show hidden (${hiddenIds.length})`}
          </button>
        )}
      </div>

      {showHidden && hiddenOnThisPage === 0 && (
        <div style={{ fontSize: 13, color: "#64748b", fontStyle: "italic" }}>
          No hidden users on this page — hidden users may be on other pages.
        </div>
      )}

      {visibleUsers.map(u => (
        <div key={u.id} style={{ background: "#1e293b", borderRadius: 10, padding: 14, border: "1px solid #334155" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#f8fafc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</div>
            <div style={{ fontSize: 11, color: "#64748b", flexShrink: 0 }}>{fmtDate(u.created_at)}</div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            <Pill label={u.subscription_status ?? "no sub"} />
            {u.has_bank && <Pill label="bank ✓" />}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <ActionBtn label="Resend link" disabled={acting === u.id + "-link"}
              onClick={() => runAction(u.id + "-link", "/api/admin/users/resend-link", { email: u.email }, "Magic link sent!")} />
            {u.has_bank && (
              <ActionBtn label="Disconnect bank" disabled={acting === u.id + "-bank"} color="#f97316"
                onClick={() => runAction(u.id + "-bank", "/api/admin/users/disconnect-bank", { user_id: u.id }, "Bank disconnected")} />
            )}
            {showHidden ? (
              <ActionBtn label="Unhide" disabled={false} color="#22c55e"
                onClick={() => unhideUser(u.id)} />
            ) : (
              <ActionBtn label="Hide" disabled={false} color="#64748b"
                onClick={() => hideUser(u.id)} />
            )}
          </div>
        </div>
      ))}

      {total > PAGE && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => setOffset(Math.max(0, offset - PAGE))} disabled={offset === 0}
            style={{ ...paginationBtn, opacity: offset === 0 ? 0.4 : 1 }}>← Prev</button>
          <span style={{ fontSize: 12, color: "#64748b" }}>{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
          <button onClick={() => setOffset(offset + PAGE)} disabled={offset + PAGE >= total}
            style={{ ...paginationBtn, opacity: offset + PAGE >= total ? 0.4 : 1 }}>Next →</button>
        </div>
      )}
    </div>
  );
}
